import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Badge, Modal, Spinner, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Card from '../../../components/Card';
import NoDataFound from '../../../components/noDataFound';
import StripeCardForBilling from '../../../components/stripe/stripeCardForBilling';
import QuickbooksCardForBilling from '../../../components/quickbooks/quickbooksCardForBilling';
import PadSetupForBilling from '../../../components/stripe/padSetupForBilling';
import PadPayForBilling from '../../../components/stripe/padPayForBilling';
import BillingPlan from '../../../components/billing/billingPlan';
import { apiHandler } from '../../../api/apihandler';
import {
  BILLING_OVERVIEW, BILLING_INVOICES, BILLING_INVOICE_DETAIL,
  BILLING_TRANSACTIONS, BILLING_CREDITS, TEACHERS_CLASS_STUDENTS_API,
  BILLING_PAD_STATUS, BILLING_PAD_CANCEL,
} from '../../../api/constants';
import { formatDisplayDate } from '../../../utilities/displayFormat';
import './billing.css';
import {
  isParentLoginProfile, isTeacherLoginProfile,
  getSelectedChildProfile, setStoredParentStudentId,
} from '../../../utilities/teacherModulePermissions';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

// Payment method labels shown to admins/teachers via the transaction
// history and payment records - matches BillingPayment::payment_method
// exactly (BillingPayment::METHOD_LABELS in the admin), never invented.
const PAYMENT_METHOD_LABELS = {
  0: 'Stripe',
  1: 'QuickBooks',
  2: 'Manual',
  3: 'Other',
  4: 'Bank Debit (PAD)',
  5: 'Cash',
  6: 'Cheque',
  7: 'Interac e-Transfer',
};

const INVOICE_STATUS = {
  0: { label: 'Draft', variant: 'secondary' },
  1: { label: 'Issued', variant: 'info' },
  2: { label: 'Paid', variant: 'success' },
  3: { label: 'Partial', variant: 'warning' },
  4: { label: 'Overdue', variant: 'danger' },
};

// billing_credits.status (see the admin's BillingCredit model).
const CREDIT_STATUS_LABEL = {
  0: 'Available',
  1: 'Fully Applied',
  2: 'Expired',
  3: 'Voided',
};

const TRANSACTION_TYPE = {
  invoice: { label: 'Invoice', icon: 'ri-file-list-3-line', color: 'text-primary' },
  subsidy: { label: 'Subsidy', icon: 'ri-hand-coin-line', color: 'text-success' },
  payment: { label: 'Payment', icon: 'ri-bank-card-line', color: 'text-success' },
  credit: { label: 'Credit / Refund Adjustment', icon: 'ri-refund-2-line', color: 'text-success' },
  adjustment: { label: 'Billing Adjustment', icon: 'ri-edit-2-line', color: 'text-warning' },
};

function isFailedResponse(res) {
  return res?.isAxiosError || (res?.response && res?.data === undefined);
}

function money(value) {
  const n = Number(value || 0);
  // Sign before the dollar sign: -$20.00, not $-20.00.
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;
}

const Billing = () => {
  let profileData = useSelector((state) => state.setting.setting.user_data);
  profileData = profileData ? JSON.parse(profileData) : null;

  const isParent = isParentLoginProfile(profileData);
  const isTeacher = isTeacherLoginProfile(profileData);
  // A parent pays for their selected child; a student can pay their own
  // balance (Phase 53 student self-pay). Teachers are read-only - the server
  // rejects a teacher's payment regardless (resolvePaymentActor).
  const canPay = !isTeacher;

  // Phase 52 - mobile WebView bridge. The harmonydaycare-app PAD setup
  // screen opens this exact page inside an in-app WebView with the
  // mobile session's own JWT attached as a URL param (same token format/
  // header the web app itself already uses - see apihandler.js's
  // `x-auth-token` / `uzoneAuthToken`). This is the ONLY place that token
  // is read from the URL, and it is written into the SAME localStorage key
  // the rest of this app already reads for every API call - no new
  // authentication mechanism, no mobile-specific backend change. Runs
  // once, before any billing data is fetched below.
  const [mobileWebviewMode] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const mobileToken = params.get('mobile_token');
      if (mobileToken) {
        localStorage.setItem('uzoneAuthToken', mobileToken);
      }
      // The native app's own apiHandler auto-attaches the parent's
      // currently-selected child (redux `activeStudentId`) to every call -
      // this WebView session has no such redux store, so that selection has
      // to ride along in the URL too, the same way the auth token does.
      // Without it, a parent session's billing calls arrive with no
      // student_id and the backend rejects them outright (it has no default
      // child to fall back to).
      const mobileStudentId = params.get('student_id');
      if (mobileStudentId) {
        setStoredParentStudentId(mobileStudentId);
      }
      return params.get('pad_setup') === '1';
    } catch (e) {
      return false;
    }
  });

  const signalPadReturnToMobile = () => {
    if (!mobileWebviewMode) {
      return;
    }
    // PadSetupScreen's WebView watches for this exact marker in the URL
    // (onNavigationStateChange) to know the flow is done and pop back to
    // the native billing screen - see padSetupScreen.js.
    const url = new URL(window.location.href);
    url.searchParams.set('pad_return', '1');
    window.history.replaceState(null, '', url.toString());
  };

  const [teacherStudents, setTeacherStudents] = useState([]);
  const [teacherStudentsLoading, setTeacherStudentsLoading] = useState(false);
  const [selectedTeacherStudentId, setSelectedTeacherStudentId] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [plan, setPlan] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [credits, setCredits] = useState([]);
  const [activityTab, setActivityTab] = useState('invoices');

  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const [showPayModal, setShowPayModal] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('stripe');
  // 'full' = pay exactly the outstanding balance (server-computed via
  // pay_full_balance); 'custom' = a partial amount typed by the payer.
  const [payMode, setPayMode] = useState('full');

  // Phase 52 - Stripe ACSS Debit/Canadian PAD mandate/processing state,
  // read-only from this component's perspective - every state change is
  // driven by the backend (PadController/webhook), this only ever reflects
  // what the server reports.
  const [padMandate, setPadMandate] = useState(null);
  const [padProcessingPayment, setPadProcessingPayment] = useState(null);
  const [padStatusLoading, setPadStatusLoading] = useState(false);

  // Teachers must explicitly pass student_id on every billing call - unlike
  // a parent session, apiHandler's withParentStudentId does not auto-attach
  // it for a teacher. A parent/student session sends {} and relies on the
  // existing auto-scoping (parent) or the backend's own-account fallback
  // (student).
  const requestScope = useCallback(() => {
    if (isTeacher) {
      return selectedTeacherStudentId ? { student_id: selectedTeacherStudentId } : null;
    }
    return {};
  }, [isTeacher, selectedTeacherStudentId]);

  useEffect(() => {
    if (!isTeacher) return;
    let cancelled = false;
    (async () => {
      setTeacherStudentsLoading(true);
      try {
        const res = await apiHandler(TEACHERS_CLASS_STUDENTS_API, 'POST', {});
        if (cancelled) return;
        if (isFailedResponse(res)) return;
        const list = res?.data?.data?.students;
        setTeacherStudents(Array.isArray(list) ? list : []);
      } finally {
        if (!cancelled) setTeacherStudentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTeacher]);

  const loadBillingData = useCallback(async () => {
    const scope = requestScope();
    if (scope === null) {
      // Teacher hasn't picked a student yet - nothing to load.
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const [overviewRes, invoicesRes, transactionsRes, creditsRes] = await Promise.all([
        apiHandler(BILLING_OVERVIEW, 'POST', { ...scope }),
        apiHandler(BILLING_INVOICES, 'POST', { ...scope, limit: 12 }),
        apiHandler(BILLING_TRANSACTIONS, 'POST', { ...scope }),
        apiHandler(BILLING_CREDITS, 'POST', { ...scope }),
      ]);

      if ([overviewRes, invoicesRes, transactionsRes, creditsRes].some(isFailedResponse)) {
        setLoadError('Unable to load billing information. Please try again.');
        return;
      }

      const overview = overviewRes?.data?.data?.overview || null;
      setSummary(overview ? { ...overview.balance, last_payment: overview.last_payment } : null);
      setPlan(overview);
      const invoiceList = invoicesRes?.data?.data?.invoices;
      setInvoices(Array.isArray(invoiceList?.data) ? invoiceList.data : (Array.isArray(invoiceList) ? invoiceList : []));
      setTransactions(Array.isArray(transactionsRes?.data?.data?.transactions) ? transactionsRes.data.data.transactions : []);
      setCredits(Array.isArray(creditsRes?.data?.data?.credits) ? creditsRes.data.data.credits : []);
    } catch (err) {
      setLoadError('Unable to load billing information. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [requestScope]);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  // Mobile WebView handoff (Phase 52): the PAD setup screen is the whole
  // reason this page was opened - skip the normal "tap Pay Now" step and
  // land directly in the modal with PAD preselected, once the summary
  // (and therefore the outstanding amount) has loaded.
  useEffect(() => {
    if (mobileWebviewMode && summary && !showPayModal) {
      setPayAmount(summary?.outstanding_balance && Number(summary.outstanding_balance) > 0 ? summary.outstanding_balance : '');
      setPayMethod('pad');
      setShowPayModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileWebviewMode, summary]);

  const loadPadStatus = useCallback(async () => {
    if (!canPay) return;
    setPadStatusLoading(true);
    try {
      const res = await apiHandler(BILLING_PAD_STATUS, 'POST', { ...requestScope() });
      if (isFailedResponse(res)) return;
      setPadMandate(res?.data?.data?.mandate || null);
      setPadProcessingPayment(res?.data?.data?.processing_payment || null);
    } finally {
      setPadStatusLoading(false);
    }
  }, [canPay, requestScope]);

  useEffect(() => {
    loadPadStatus();
  }, [loadPadStatus]);

  const handleCancelPad = async () => {
    if (!padMandate) return;
    const confirmed = window.confirm('Cancel your bank payment (PAD) authorization? No future bank debits will be initiated under it.');
    if (!confirmed) return;
    const res = await apiHandler(BILLING_PAD_CANCEL, 'POST', {});
    if (isFailedResponse(res)) return;
    loadPadStatus();
  };

  const openInvoiceDetail = async (invoiceId) => {
    setShowInvoiceModal(true);
    setInvoiceDetailLoading(true);
    setInvoiceDetail(null);
    try {
      const scope = requestScope() || {};
      const res = await apiHandler(BILLING_INVOICE_DETAIL, 'POST', { ...scope, invoice_id: invoiceId });
      if (isFailedResponse(res)) return;
      setInvoiceDetail(res?.data?.data?.invoice || null);
    } finally {
      setInvoiceDetailLoading(false);
    }
  };

  const openPayModal = () => {
    setPayAmount(summary?.outstanding_balance && Number(summary.outstanding_balance) > 0 ? summary.outstanding_balance : '');
    setPayMode('full');
    setPayMethod('stripe');
    setShowPayModal(true);
    loadPadStatus();
  };

  const handlePaymentSuccess = () => {
    setShowPayModal(false);
    loadBillingData();
    loadPadStatus();
  };

  const handlePadSetupSuccess = () => {
    // The mandate is not ACTIVE yet (becomes so only once the
    // setup_intent.succeeded webhook is processed) - refresh status so the
    // modal can reflect "setup submitted" rather than assuming it is
    // immediately usable.
    loadPadStatus();
    signalPadReturnToMobile();
  };

  const selectedChild = isParent ? getSelectedChildProfile(profileData) : null;
  const childLabel = selectedChild ? `${selectedChild.first_name || ''} ${selectedChild.last_name || ''}`.trim() : null;

  const outstanding = Number(summary?.outstanding_balance || 0);
  const availableCredit = Number(summary?.available_credit_balance || 0);
  const netBalance = Number(summary?.net_balance || 0);

  return (
    <>
      <div id="content-page" className="content-page scroller">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Billing{childLabel ? ` — ${childLabel}` : ''}</h4>
                {isTeacher && (
                  <Badge bg="secondary"><i className="ri-eye-line me-1"></i>Read only</Badge>
                )}
              </div>
            </Col>
          </Row>

          {isTeacher && (
            <Row className="mb-3">
              <Col lg={12}>
                <Card>
                  <Card.Body>
                    <Form.Label className="fw-bold">Select a student</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search by student name..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      className="mb-2"
                    />
                    {teacherStudentsLoading ? (
                      <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                    ) : (
                      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        {teacherStudents
                          .filter((s) => !studentSearch.trim() || s.name?.toLowerCase().includes(studentSearch.trim().toLowerCase()))
                          .map((s) => (
                            <div
                              key={s.id}
                              className={`p-2 rounded mb-1 ${String(selectedTeacherStudentId) === String(s.id) ? 'bg-soft-primary' : ''}`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setSelectedTeacherStudentId(s.id)}
                            >
                              {s.name}
                            </div>
                          ))}
                        {teacherStudents.length === 0 && <div className="text-muted small">No students found.</div>}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {isTeacher && !selectedTeacherStudentId ? (
            <NoDataFound txtData="Select a student above to view their billing information." />
          ) : loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : loadError ? (
            <div className="text-center py-5 text-danger">
              <i className="ri-error-warning-line" style={{ fontSize: 32 }}></i>
              <p className="mt-2">{loadError}</p>
            </div>
          ) : (
            <>
              {/* Account: balance, payment action, credit, last payment and
                  bank (PAD) status in one place. */}
              <Card className="billing-account mb-3">
                <Card.Body className="billing-compact-body">
                  <div className="billing-stats">
                    <div className="billing-stat billing-stat-due">
                      <div className="billing-label">Amount due</div>
                      <div className="d-flex align-items-center flex-wrap gap-2">
                        <div className={`billing-amount ${outstanding > 0 ? 'text-danger' : 'text-success'}`}>{money(outstanding)}</div>
                        {outstanding > 0 ? (
                          <span className="billing-chip is-due"><i className="ri-time-line"></i>Balance due</span>
                        ) : (
                          <span className="billing-chip is-paid"><i className="ri-checkbox-circle-line"></i>All paid up</span>
                        )}
                      </div>
                      {canPay && outstanding > 0 && (
                        <button className="btn btn-primary btn-sm billing-pay-btn" onClick={openPayModal}>
                          <i className="ri-secure-payment-line me-1"></i>Pay Now
                        </button>
                      )}
                    </div>
                    <div className="billing-stat">
                      <div className="billing-label">Available credit</div>
                      <div className="billing-stat-value text-success">{money(availableCredit)}</div>
                      {availableCredit > 0 && <div className="billing-hint">Applied to your next invoice</div>}
                    </div>
                    <div className="billing-stat">
                      <div className="billing-label">Net balance</div>
                      <div className="billing-stat-value">{money(netBalance)}</div>
                      <div className="billing-hint">
                        {netBalance < 0 ? 'In credit' : netBalance > 0 ? 'Owing after credit' : 'Settled'}
                      </div>
                    </div>
                    <div className="billing-stat">
                      <div className="billing-label">Last payment</div>
                      {summary?.last_payment ? (
                        <>
                          <div className="billing-stat-value">{money(summary.last_payment.amount)}</div>
                          <div className="billing-hint">
                            {PAYMENT_METHOD_LABELS[summary.last_payment.payment_method] || 'Other'} · {formatDisplayDate(summary.last_payment.received_at)}
                          </div>
                        </>
                      ) : (
                        <div className="billing-stat-value text-muted">—</div>
                      )}
                    </div>
                  </div>

                  {canPay && padMandate && padMandate.status === 1 && (
                    <div className="billing-pad">
                      <span>
                        <i className="ri-bank-line me-1"></i>
                        <strong>Bank Payment (PAD)</strong>{' '}
                        <Badge bg="success">Active</Badge>
                        {padProcessingPayment && (
                          <span className="ms-2 text-muted small">
                            Processing: {money(padProcessingPayment.amount)}
                          </span>
                        )}
                      </span>
                      <button className="btn btn-link btn-sm text-danger p-0" disabled={padStatusLoading} onClick={handleCancelPad}>
                        Cancel Bank Payment
                      </button>
                    </div>
                  )}
                </Card.Body>
              </Card>

              <div className="billing-plan">
                <BillingPlan plan={plan} />
              </div>

              {/* Activity: invoices, transactions and credits share one card. */}
              <Card className="billing-activity mb-3">
                <Card.Header className="billing-tabs" role="tablist">
                  {[
                    { key: 'invoices', label: 'Invoices', count: invoices.length },
                    { key: 'transactions', label: 'Transactions', count: transactions.length },
                    { key: 'credits', label: 'Credits', count: credits.length },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={activityTab === tab.key}
                      className={`billing-tab ${activityTab === tab.key ? 'is-active' : ''}`}
                      onClick={() => setActivityTab(tab.key)}
                    >
                      {tab.label}
                      <span className="billing-tab-count">{tab.count}</span>
                    </button>
                  ))}
                </Card.Header>
                <Card.Body className="p-0">
                  {activityTab === 'invoices' && (
                    invoices.length === 0 ? (
                      <NoDataFound txtData="No invoices yet." />
                    ) : (
                      <div className="table-responsive">
                        <table className="table billing-table mb-0">
                          <thead>
                            <tr>
                              <th>Period</th><th className="text-end">Total</th><th className="text-end">Paid</th>
                              <th className="text-end">Balance</th><th>Status</th><th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map((inv) => (
                              <tr key={inv.id}>
                                <td className="fw-semibold">{formatDisplayDate(inv.period_start_date)} – {formatDisplayDate(inv.period_end_date)}</td>
                                <td className="text-end">{money(inv.total_parent_responsibility)}</td>
                                <td className="text-end">{money(inv.total_paid)}</td>
                                <td className={`text-end ${Number(inv.balance_due) > 0 ? 'text-danger fw-semibold' : ''}`}>{money(inv.balance_due)}</td>
                                <td>
                                  <Badge bg={INVOICE_STATUS[inv.status]?.variant || 'secondary'}>
                                    {INVOICE_STATUS[inv.status]?.label || 'Unknown'}
                                  </Badge>
                                </td>
                                <td className="text-end">
                                  <button className="btn btn-sm btn-outline-primary" onClick={() => openInvoiceDetail(inv.id)}>
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}

                  {activityTab === 'transactions' && (
                    transactions.length === 0 ? (
                      <NoDataFound txtData="No transactions yet." />
                    ) : (
                      <div className="billing-list">
                        {transactions.map((t, i) => {
                          const meta = TRANSACTION_TYPE[t.type] || { label: t.type, icon: 'ri-file-line', color: 'text-muted' };
                          const isNegativeDisplay = String(t.amount).trim().startsWith('-');
                          return (
                            <div key={i} className="billing-row">
                              <span className={`billing-row-icon ${meta.color}`}><i className={meta.icon}></i></span>
                              <div className="billing-row-text">
                                <div className="fw-semibold">{meta.label}</div>
                                <div className="text-muted small">
                                  {t.description}
                                  {t.billing_period && <> · {t.billing_period}</>}
                                </div>
                              </div>
                              <div className="text-end">
                                <div className={isNegativeDisplay ? 'text-success fw-bold' : 'fw-bold'}>
                                  {isNegativeDisplay ? '-' : ''}{money(String(t.amount).replace('-', ''))}
                                </div>
                                <div className="text-muted small">{formatDisplayDate(t.date)}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )
                  )}

                  {activityTab === 'credits' && (
                    credits.length === 0 ? (
                      <NoDataFound txtData="No available credit." />
                    ) : (
                      <div className="billing-list">
                        {credits.map((c) => (
                          <div key={c.id} className="billing-row">
                            <span className="billing-row-icon text-success"><i className="ri-refund-2-line"></i></span>
                            <div className="billing-row-text">
                              <div className="fw-semibold">{money(c.remaining_amount)} remaining</div>
                              <div className="text-muted small">Original {money(c.original_amount)} · {formatDisplayDate(c.created_at)}</div>
                            </div>
                            <Badge bg={c.status === 0 ? 'success' : 'secondary'}>
                              {CREDIT_STATUS_LABEL[c.status] || 'Unknown'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Container>
      </div>

      {/* Invoice detail modal */}
      <Modal show={showInvoiceModal} onHide={() => setShowInvoiceModal(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Invoice Details</Modal.Title></Modal.Header>
        <Modal.Body>
          {invoiceDetailLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : !invoiceDetail ? (
            <NoDataFound txtData="Unable to load invoice details." />
          ) : (
            <>
              <Row className="mb-3">
                <Col md={6}><strong>Period:</strong> {formatDisplayDate(invoiceDetail.period_start_date)} – {formatDisplayDate(invoiceDetail.period_end_date)}</Col>
                <Col md={6}><strong>Status:</strong> <Badge bg={INVOICE_STATUS[invoiceDetail.status]?.variant || 'secondary'}>{INVOICE_STATUS[invoiceDetail.status]?.label}</Badge></Col>
              </Row>
              <table className="table">
                <tbody>
                  <tr><td>Total Billed</td><td className="text-end">{money(invoiceDetail.total_gross_charge)}</td></tr>
                  <tr><td>Subsidy</td><td className="text-end">-{money(invoiceDetail.total_subsidy)}</td></tr>
                  <tr><td>Parent Responsibility</td><td className="text-end">{money(invoiceDetail.total_parent_responsibility)}</td></tr>
                  {Number(invoiceDetail.total_adjustments) !== 0 && (
                    <tr><td>Adjustments</td><td className="text-end">{money(invoiceDetail.total_adjustments)}</td></tr>
                  )}
                  <tr><td>Paid</td><td className="text-end">-{money(invoiceDetail.total_paid)}</td></tr>
                  <tr><td>Credit Applied</td><td className="text-end">-{money(invoiceDetail.credit_applied)}</td></tr>
                  <tr className="fw-bold"><td>Balance Due</td><td className="text-end">{money(invoiceDetail.balance_due)}</td></tr>
                </tbody>
              </table>
              {Array.isArray(invoiceDetail.days) && invoiceDetail.days.length > 0 && (
                <>
                  <h6 className="mt-3">Day-by-day</h6>
                  <div className="table-responsive" style={{ maxHeight: 360, overflowY: 'auto' }}>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Date</th><th>Day</th><th className="text-end">Charge</th>
                          <th className="text-end">Subsidy</th><th className="text-end">You pay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceDetail.days.map((d) => (
                          <tr key={d.date}>
                            <td>{formatDisplayDate(d.date)}</td>
                            <td>
                              {d.day}
                              {d.rate_type && <div className="text-muted small">{d.rate_type} rate</div>}
                              {d.absence_allowance_used && <Badge bg="info" className="ms-1">Covered absence</Badge>}
                            </td>
                            <td className="text-end">{money(d.charge)}</td>
                            <td className="text-end text-success">
                              {Number(d.subsidy) > 0 ? `-${money(d.subsidy)}` : '—'}
                              {d.subsidy_rule && <div className="text-muted small">{d.subsidy_rule}</div>}
                            </td>
                            <td className="text-end fw-semibold">{money(d.you_pay)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Pay Now modal */}
      <Modal show={showPayModal} onHide={() => setShowPayModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Pay Your Balance</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="pay-section-label">Amount</div>
          <label htmlFor="pay-mode-full" className={`pay-option ${payMode === 'full' ? 'is-selected' : ''}`}>
            <input
              type="radio"
              id="pay-mode-full"
              name="pay-mode"
              className="form-check-input"
              checked={payMode === 'full'}
              onChange={() => { setPayMode('full'); setPayAmount(outstanding.toFixed(2)); }}
            />
            <span className="pay-option-text">
              <span className="pay-option-title d-block">Pay full balance</span>
              <span className="pay-option-sub">Clears everything you owe right now</span>
            </span>
            <span className="pay-option-amount">{money(outstanding)}</span>
          </label>
          <label
            htmlFor="pay-mode-custom"
            className={`pay-option ${payMode === 'custom' ? 'is-selected' : ''} ${payMethod === 'pad' ? 'is-disabled' : ''}`}
          >
            <input
              type="radio"
              id="pay-mode-custom"
              name="pay-mode"
              className="form-check-input"
              checked={payMode === 'custom'}
              disabled={payMethod === 'pad'}
              onChange={() => setPayMode('custom')}
            />
            <span className="pay-option-text">
              <span className="pay-option-title d-block">Pay a different amount</span>
              <span className="pay-option-sub">
                {payMethod === 'pad' ? 'Bank payments (PAD) always pay the full balance' : `Any amount up to ${money(outstanding)}`}
              </span>
            </span>
          </label>
          {payMode === 'custom' && payMethod !== 'pad' && (
            <div className="mb-2">
              <div className="input-group">
                <span className="input-group-text">$</span>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={outstanding > 0 ? outstanding : undefined}
                  value={payAmount}
                  placeholder="0.00"
                  onChange={(e) => setPayAmount(e.target.value)}
                />
              </div>
              <Form.Text className="text-muted">Payments go to the oldest invoice first.</Form.Text>
            </div>
          )}

          <div className="pay-section-label mt-3">Payment method</div>
          <div className="pay-methods">
            {[
              { key: 'stripe', icon: 'ri-bank-card-line', title: 'Card', sub: 'Visa · Mastercard' },
              { key: 'quickbooks', icon: 'ri-file-list-3-line', title: 'QuickBooks', sub: 'Saved card' },
              { key: 'pad', icon: 'ri-bank-line', title: 'Bank (PAD)', sub: 'Pre-authorized debit' },
            ].map((m) => (
              <button
                key={m.key}
                type="button"
                className={`pay-method ${payMethod === m.key ? 'is-selected' : ''}`}
                aria-pressed={payMethod === m.key}
                onClick={() => {
                  setPayMethod(m.key);
                  if (m.key === 'pad') { setPayMode('full'); setPayAmount(outstanding.toFixed(2)); }
                }}
              >
                <i className={m.icon}></i>
                <span>{m.title}</span>
                <small>{m.sub}</small>
              </button>
            ))}
          </div>

          {(payMode === 'full' ? outstanding : Number(payAmount)) > 0 && (
            <div className="pay-summary">
              <span>
                You&apos;ll pay
                {' '}{payMethod === 'stripe' ? 'by card' : payMethod === 'quickbooks' ? 'with QuickBooks' : 'from your bank'}
              </span>
              <strong>{money(payMode === 'full' ? outstanding : payAmount)}</strong>
            </div>
          )}

          {payMethod === 'stripe' && (payMode === 'full' ? outstanding : Number(payAmount)) > 0 && (
            <Elements stripe={stripePromise}>
              <StripeCardForBilling
                amount={payMode === 'full' ? outstanding : payAmount}
                payFullBalance={payMode === 'full'}
                onSuccess={handlePaymentSuccess}
                onCancel={() => setShowPayModal(false)}
              />
            </Elements>
          )}
          {payMethod === 'quickbooks' && (payMode === 'full' ? outstanding : Number(payAmount)) > 0 && (
            <QuickbooksCardForBilling
              amount={payMode === 'full' ? outstanding : payAmount}
              payFullBalance={payMode === 'full'}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setShowPayModal(false)}
            />
          )}
          {payMethod === 'pad' && outstanding > 0 && (
            padMandate && padMandate.status === 1 ? (
              <PadPayForBilling
                outstanding={outstanding}
                onSuccess={() => { handlePaymentSuccess(); signalPadReturnToMobile(); }}
                onCancel={() => { setShowPayModal(false); signalPadReturnToMobile(); }}
              />
            ) : (
              <Elements stripe={stripePromise}>
                <PadSetupForBilling
                  defaultName={profileData ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() : ''}
                  defaultEmail={profileData?.email || ''}
                  onSuccess={handlePadSetupSuccess}
                  onCancel={() => { setShowPayModal(false); signalPadReturnToMobile(); }}
                />
              </Elements>
            )
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Billing;
